import {DataTable} from "../DataTable/DataTable";
import {TagCategoryEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useTagCategoryDataTableFilter} from "../TagCategoryDataTable/useTagCategoryDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {useNavigate} from "react-router-dom";
import {useTagCategoryAction} from "../../menu/TagCategoryActionMenu/useTagCategoryAction";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {YesNoText} from "../../text/YesNoText/YesNoText";

const EMPTY_ARRAY: TagCategoryEntity[] = []

export type TagCategoryDataTableProps = EntityDataTableProps<TagCategoryEntity>

export const TagCategoryDataTable = (props: TagCategoryDataTableProps) => {
  const {
    records: defs = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {filteredRecords, filterConfig} = useTagCategoryDataTableFilter({
    records: defs
  })

  const sortConfig = useMemo(() => {
    return {
      "label": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.label?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'label', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    onTagCategoryActionClick
  } = useTagCategoryAction()

  const onViewActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: TagCategoryEntity) => {
    e.stopPropagation()
    navigate(`/tags/categories/profile?id=${record.id}`)
  }, [navigate])

  const onEditActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: TagCategoryEntity) => {
    e.stopPropagation()
    onTagCategoryActionClick(record, 'EDIT')
  }, [onTagCategoryActionClick])

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: TagCategoryEntity) => {
    e.stopPropagation()
    onTagCategoryActionClick(record, 'DELETE')
  }, [onTagCategoryActionClick])

  const columns = useMemo(() => {
    const values: DataTableColumn<TagCategoryEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "label"),
        title: t("common.entity.tagcategory.table.column.label", "Label"),
        accessor: 'label',
        width: 100,
        sortable: true,
      },
      {
        title: t("common.entity.tagcategory.table.column.donotuse", "Enabled"),
        accessor: 'doNotUse',
        width: 100,
        sortable: true,
        render: ({doNotUse}) => {
          return <YesNoText value={!doNotUse}/>
        }
      },
      {
        title: '',
        accessor: "actions",
        width: 50,
        textAlignment: 'right',
        render: (def) => (
          <EntityActionGroup
            permissionPrefix="tagcategory"
            view={false}
            onViewActionClick={(e) => onViewActionClick(e, def)}
            onEditActionClick={(e) => onEditActionClick(e, def)}
            onDeleteActionClick={(e) => onDeleteActionClick(e, def)}/>
        )
      }
    ]

    return values
  }, [filterConfig, onDeleteActionClick, onEditActionClick, onViewActionClick, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
